import '../css/OrganizationAdminPage.less';

import React, {useState, useMemo, useCallback} from 'react';

import {useViewer} from '../state/viewer/hooks';
import WandbLoader from '../components/WandbLoader';
import {
  useAllOrganizationsQuery,
  useSubscriptionPlansQuery,
  useCreateCustomerTrialMutation,
  AllOrganizationsQuery,
  OrganizationSubscriptionType,
} from '../generated/graphql';
import config from '../config';
import {
  Button,
  Modal,
  Input,
  Dropdown,
  Message,
  Popup,
} from 'semantic-ui-react';
import _ from 'lodash';
import {
  doNotRetryContext,
  propagateErrorsContext,
  extractErrorMessageFromApolloError,
} from '../util/errors';
import {toast} from 'react-toastify';
import {QueryResult} from 'react-apollo';
import {slugFormat} from '../util/text';
import * as urls from '../util/urls';
import NoMatch from '../components/NoMatch';
import LegacyWBIcon from '../components/elements/LegacyWBIcon';
import EditUserModal from '../components/EditUserModal';
import UpdateOrganizationSubscriptionSeatModal from '../components/UpdateSeatCountModal';
import {CreateTeamModal} from '../components/CreateTeamModal';
import makeComp from '../util/profiler';

const STRIPE_DASHBOARD_PREFIX =
  config.ENVIRONMENT_NAME === 'development' ||
  config.ENVIRONMENT_NAME === 'integration'
    ? 'https://dashboard.stripe.com/test'
    : 'https://dashboard.stripe.com';

const OrganizationAdminPage: React.FC = makeComp(
  () => {
    const viewer = useViewer();
    const organizations = useAllOrganizationsQuery({
      fetchPolicy: 'network-only',
    });

    const [modalOpen, setModalOpen] = useState(false);

    if (!viewer) {
      return <WandbLoader></WandbLoader>;
    }
    if (!viewer.admin) {
      return <NoMatch />;
    }

    return (
      <div className="org-admin-page">
        <div className="org-admin-page-container">
          <h1>All Organizations</h1>
          <OrganizationList organizationsQueryResult={organizations} />
          <div className="divider" />
          <Button
            className="create-trial-button"
            color="grey"
            onClick={() => setModalOpen(true)}>
            Create a trial
          </Button>
          <Modal
            className="trial-modal"
            open={modalOpen}
            onClose={() => setModalOpen(false)}>
            <CreateTrialForm
              onTrialCreated={() => {
                setModalOpen(false);
                organizations.refetch();
              }}
            />
          </Modal>
        </div>
      </div>
    );
  },
  {id: 'OrganizationAdminPage'}
);

const prettifyPlanName = (name: string) => {
  return _.capitalize(name.replace('_', ' '));
};

interface CreateTrialFormProps {
  onTrialCreated(): void;
}

const CreateTrialForm: React.FC<CreateTrialFormProps> = makeComp(
  props => {
    const plans = useSubscriptionPlansQuery();
    const [formDisabled, setFormDisabled] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [selectedSeats, setSelectedSeats] = useState<number | null>(null);
    const [trialUsername, setTrialUsername] = useState<string | null>(null);
    const [trialOrg, setTrialOrg] = useState<string | null>(null);
    const [trialPeriod, setTrialPeriod] = useState<number | null>(null);

    const [createCustomerTrialMutation] = useCreateCustomerTrialMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

    const planOptions = useMemo(() => {
      if (plans.loading || plans.data == null) {
        return [];
      }

      return plans.data.plans.map(p => ({
        key: p?.name,
        text: prettifyPlanName(p?.name ?? ''),
        value: p?.id,
      }));
    }, [plans]);

    const planSeatLimit = useMemo(() => {
      if (selectedPlan == null || plans.loading || plans.data == null) {
        return null;
      }

      return plans.data.plans.find(p => p?.id === selectedPlan)?.maxSeats;
    }, [selectedPlan, plans]);

    const possibleUnits = useMemo(() => {
      if (planSeatLimit == null) {
        return [];
      }

      return _.range(1, planSeatLimit + 1).map(i => ({
        key: i,
        text: i,
        value: i,
      }));
    }, [planSeatLimit]);

    const onSubmit = useCallback(async () => {
      setFormDisabled(true);
      setError(null);

      if (
        [
          selectedPlan,
          selectedSeats,
          trialUsername,
          trialOrg,
          trialPeriod,
        ].some(v => v == null)
      ) {
        setError('You must fill out every field.');
        setFormDisabled(false);
        return;
      }

      try {
        const createTrialResult = await createCustomerTrialMutation({
          variables: {
            planId: selectedPlan!,
            userName: trialUsername!,
            newOrganizationName: trialOrg!,
            quantity: selectedSeats!,
            trialDays: trialPeriod!,
          },
        });

        if (createTrialResult.errors != null) {
          setError(
            `Error creating trial: ${createTrialResult.errors.join(', ')}`
          );
          return;
        }

        toast('Successfully created organization and trial.');
        props.onTrialCreated();
      } catch (err) {
        const errMsg = extractErrorMessageFromApolloError(err) ?? err.message;
        setError(`Error creating trial: ${errMsg}`);
        return;
      } finally {
        setFormDisabled(false);
      }
    }, [
      createCustomerTrialMutation,
      props,
      selectedPlan,
      selectedSeats,
      trialOrg,
      trialPeriod,
      trialUsername,
    ]);

    return (
      <>
        <div className="trial-form">
          <h2>Create a trial for a customer</h2>
          {error && <Message negative content={error} />}
          <div className="trial-input-group">
            <div className="dropdown-group">
              <Dropdown
                disabled={formDisabled}
                className="trial-input"
                selection
                value={selectedPlan ?? undefined}
                placeholder={'Select a plan'}
                options={planOptions}
                onChange={(_1, data) => {
                  setSelectedSeats(1);
                  setSelectedPlan(data.value as string);
                }}
              />
              <Dropdown
                disabled={formDisabled}
                className="trial-input"
                selection
                value={selectedSeats ?? undefined}
                placeholder={'Select # of seats'}
                options={possibleUnits}
                onChange={(_1, data) => setSelectedSeats(data.value as number)}
              />
            </div>
            <Input
              disabled={formDisabled}
              className="trial-input"
              placeholder="Username"
              onChange={(_1, data) => setTrialUsername(data.value)}
            />
            <Input
              disabled={formDisabled}
              className="trial-input"
              placeholder="Organization name"
              value={trialOrg}
              onChange={(_1, data) => setTrialOrg(slugFormat(data.value))}
            />
            <Input
              disabled={formDisabled}
              className="trial-input"
              type="number"
              placeholder="Trial period in days"
              onChange={(_1, data) => setTrialPeriod(parseInt(data.value, 10))}
            />
            <Button
              disabled={formDisabled}
              className="submit-button"
              onClick={onSubmit}>
              Create trial
            </Button>
          </div>
        </div>
      </>
    );
  },
  {id: 'CreateTrialForm'}
);

interface OrganizationListProps {
  organizationsQueryResult: QueryResult<AllOrganizationsQuery>;
}

const OrganizationList: React.FC<OrganizationListProps> = makeComp(
  props => {
    type OrganizationResult = AllOrganizationsQuery['organizations'][0];
    const organizations = props.organizationsQueryResult;
    const [
      selectedOrganization,
      setSelectedOrganization,
    ] = useState<OrganizationResult | null>(null);
    const selectedOrgOption = useMemo(() => {
      if (selectedOrganization == null) {
        return [];
      }
      return [
        {
          text: selectedOrganization?.name ?? '',
          value: selectedOrganization?.id ?? '',
        },
      ];
    }, [selectedOrganization]);
    const [editUserModalOpen, setEditUserModalOpen] = useState(false);
    const [createTeamModalOpen, setCreateTeamModalOpen] = useState(false);
    const [
      updateOrganizationSubscriptionSeatModalOpen,
      setUpdateOrganizationSubscriptionSeatModalOpen,
    ] = useState(false);

    if (organizations.loading) {
      return <WandbLoader />;
    }

    const displayableOrgs =
      organizations.data?.organizations.filter(
        o =>
          !o?.billingUser.email?.includes('@wandb.com') &&
          o?.billingUser.email !== 'inown@pm.me' &&
          o?.billingUser.email !== 'adrnswanberg@gmail.com'
      ) ?? [];

    const getPrimarySubscriptionFromOrg = (org: OrganizationResult | null) => {
      return org?.subscriptions[0];
    };
    const getOrgStripeLink = (org: OrganizationResult | null) => {
      if (org?.stripeBillingInfo?.stripeSubscriptionId == null) {
        return 'N/A';
      }
      return `${STRIPE_DASHBOARD_PREFIX}/subscriptions/${org?.stripeBillingInfo?.stripeSubscriptionId}`;
    };
    const selectedOrgPrimarySubscription = getPrimarySubscriptionFromOrg(
      selectedOrganization
    );

    return (
      <>
        <EditUserModal
          open={editUserModalOpen}
          organization={
            selectedOrganization && {
              id: selectedOrganization.id,
              name: selectedOrganization.name,
              billingUserName:
                selectedOrganization.billingUser.username ?? null,
              subscriptionStatus:
                selectedOrganization.stripeBillingInfo?.status ?? '',
              cancelAtPeriodEnd:
                selectedOrganization.stripeBillingInfo?.cancelAtPeriodEnd ??
                false,
              members: selectedOrganization.members,
            }
          }
          onAddUser={() => organizations.refetch()}
          onRemoveUser={() => organizations.refetch()}
          onUpdateOrganizationUser={() => organizations.refetch()}
          onClose={() => setEditUserModalOpen(false)}
        />
        <CreateTeamModal
          open={createTeamModalOpen}
          orgOptions={selectedOrgOption}
          teamAdminUsername={
            selectedOrganization?.billingUser.username ?? undefined
          }
          onClose={() => setCreateTeamModalOpen(false)}
          onCreate={() => organizations.refetch()}
        />
        <UpdateOrganizationSubscriptionSeatModal
          open={updateOrganizationSubscriptionSeatModalOpen}
          subscriptionID={selectedOrgPrimarySubscription?.id}
          subscriptionCurrentSeats={selectedOrgPrimarySubscription?.seats}
          stripeLink={getOrgStripeLink(selectedOrganization)}
          onUpdate={() => {
            organizations.refetch();
            toast('Success! Refreshing latest data from backend services...', {
              position: 'top-right',
              autoClose: 25000,
            });
            setUpdateOrganizationSubscriptionSeatModalOpen(false);
          }}
          onClose={() => setUpdateOrganizationSubscriptionSeatModalOpen(false)}
        />
        <div className="org-table">
          <div className="org-table-row org-table-header">
            <div className="org-table-cell">NAME</div>
            <div className="org-table-cell">PLAN</div>
            <div className="org-table-cell short">SEATS</div>
            <div className="org-table-cell">MEMBERS</div>
            <div className="org-table-cell">TEAMS</div>
            <div className="org-table-cell">BILLING USER</div>
            <div className="org-table-cell">STATUS</div>
            <div className="org-table-cell">STRIPE LINK</div>
            <div className="org-table-cell">PAYMENT METHOD?</div>
            <div className="org-table-cell">ACTIONS</div>
          </div>
          {displayableOrgs.map(org => {
            if (org == null) {
              return <></>;
            }
            const primarySubscription = getPrimarySubscriptionFromOrg(org);
            const nonStripeTrialExpiration = primarySubscription?.expiresAt
              ? new Date(
                  primarySubscription.expiresAt + 'Z'
                ).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
              : null;
            const nonStripeTrialType = _.capitalize(
              primarySubscription?.subscriptionType.replace('_', ' ')
            );
            const rawBillingDate = org.stripeBillingInfo?.currentPeriodEnd;
            const nextBillingDate = rawBillingDate
              ? new Date(rawBillingDate + 'Z').toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
              : null;
            const billingUsername = org.billingUser.username;

            const hasPaymentMethod =
              org.stripeBillingInfo?.paymentMethod != null;

            const stripeLink = getOrgStripeLink(org);

            return (
              <div className="org-table-row" key={org.id}>
                <div className="org-table-cell">{org?.name}</div>
                <div className="org-table-cell">
                  {prettifyPlanName(primarySubscription?.plan.name ?? '')}
                </div>
                <div className="org-table-cell short">
                  {primarySubscription?.seats}
                </div>
                <div className="org-table-cell">
                  {org?.members.map(m => m.username).join(', ')}
                </div>
                <div className="org-table-cell">
                  {_.dropRight(
                    _.flatten(
                      org?.teams.map(t => [
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={urls.teamPage(t.name)}>
                          {t.name}
                        </a>,
                        ', ',
                      ])
                    )
                  )}
                </div>
                <div className="org-table-cell">
                  {billingUsername && (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={urls.entity(billingUsername)}>
                      {billingUsername}
                    </a>
                  )}
                  {` (${org?.billingUser.email})`}
                </div>
                <div className="org-table-cell">
                  {org?.stripeBillingInfo?.status}
                  {org?.stripeBillingInfo?.status === 'trialing' &&
                    ` (ends ${nextBillingDate})`}
                  {nextBillingDate && ` (bills again on ${nextBillingDate})`}
                  {primarySubscription?.subscriptionType ===
                    OrganizationSubscriptionType.Enterprise &&
                    'Active (Enterprise)'}
                  {primarySubscription?.subscriptionType ===
                    OrganizationSubscriptionType.Academic &&
                    'Active (Academic)'}
                  {nonStripeTrialExpiration &&
                    nonStripeTrialType &&
                    `${nonStripeTrialType} expires on ${nonStripeTrialExpiration}`}
                </div>
                <div className="org-table-cell">
                  {org.stripeBillingInfo?.stripeSubscriptionId ? (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={stripeLink}>
                      {org.stripeBillingInfo?.stripeSubscriptionId}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </div>
                <div className="org-table-cell">
                  {hasPaymentMethod ? 'Yes' : 'No'}
                </div>
                <div className="org-table-cell">
                  <Popup
                    position={'top center'}
                    trigger={
                      <span>
                        <LegacyWBIcon
                          data-test="edit-user-button"
                          link
                          style={{marginRight: 4}}
                          name="edit"
                          onClick={() => {
                            setSelectedOrganization(org);
                            setEditUserModalOpen(true);
                          }}
                        />
                      </span>
                    }
                    content={'Edit organization members'}
                    size="small"
                  />
                  <Popup
                    position={'top center'}
                    trigger={
                      <span>
                        <LegacyWBIcon
                          data-test="create-team-button"
                          link
                          style={{marginRight: 4}}
                          name="plus"
                          onClick={() => {
                            setSelectedOrganization(org);
                            setCreateTeamModalOpen(true);
                          }}
                        />
                      </span>
                    }
                    content={'Create new team'}
                    size="small"
                  />
                  {primarySubscription && (
                    <Popup
                      position={'top center'}
                      trigger={
                        <LegacyWBIcon
                          data-test="change-seats-button"
                          link
                          style={{marginRight: 4}}
                          name="share"
                          onClick={() => {
                            setSelectedOrganization(org);
                            setUpdateOrganizationSubscriptionSeatModalOpen(
                              true
                            );
                          }}
                        />
                      }
                      content={'Change allocated seats'}
                      size="small"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  },
  {id: 'OrganizationList'}
);

export default OrganizationAdminPage;
